import * as Sentry from '@sentry/react';

/**
 * Thumbnail size URL used to detect persistence after avatar/cover upload.
 * Avatar: 190 square; cover: 460 wide.
 */
export function getThumbnailUrl(block, field) {
  if (!block || !block.images) {
    return undefined;
  }
  if (field === 'avatars') {
    return ((block.images['190'] || {}).square || {}).main;
  }
  if (field === 'covers') {
    return ((block.images['460'] || {}).wide || {}).main;
  }
  return undefined;
}

/**
 * Poll until avatars/covers leave placeholder and the size URL changes.
 *
 * @param {Object} options
 * @param {Function} options.fetchUser  () => Promise<user>
 * @param {'avatars'|'covers'} options.field
 * @param {string|undefined} options.previousUrl
 * @param {number} [options.timeoutMs=60000]
 * @param {number} [options.intervalMs=2000]
 * @param {Function} [options.now]
 * @param {Function} [options.delay]
 * @returns {Promise<Object>} resolved media block (avatars or covers)
 */
export function waitForThumbnail({
  fetchUser,
  field,
  previousUrl,
  timeoutMs = 60000,
  intervalMs = 2000,
  now = () => Date.now(),
  delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
}) {
  const started = now();

  const poll = () =>
    Promise.resolve(fetchUser()).then((user) => {
      const block = (user && user[field]) || {};
      const url = getThumbnailUrl(block, field);
      if (block.placeholder === false && url && url !== previousUrl) {
        return block;
      }
      if (now() - started > timeoutMs) {
        const err = new Error('thumbnail timeout');
        err.code = 'thumbnail_timeout';
        return Promise.reject(err);
      }
      return delay(intervalMs).then(poll);
    });

  return poll();
}

export function trackProfilePhotoSave({ outcome, field, bytes, duration_ms }) {
  if (
    typeof window !== 'undefined' &&
    window.posthog &&
    typeof window.posthog.capture === 'function'
  ) {
    window.posthog.capture('freelancer-saves-profile-photo', {
      outcome,
      field,
      bytes,
      duration_ms,
    });
  }
}

export function breadcrumbProfilePhoto(message, data = {}) {
  if (typeof Sentry.addBreadcrumb === 'function') {
    Sentry.addBreadcrumb({
      category: 'profile-media',
      message,
      data,
      level: 'info',
    });
  }
}
